import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { FiActivity, FiRefreshCw, FiEye, FiChevronRight, FiChevronDown, FiMonitor, FiSearch } from 'react-icons/fi';
import { getInstances } from '../../services/monitorService';
import {
  fmtMoney, fmtProfit, fmtNum, fmtAgo, profitClass
} from '../../utils/format';

// 在线状态徽章
const OnlineBadge = ({ online }) => (
  online ? (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-[#dafbe1] text-[#1a7f37]">
      <span className="w-1.5 h-1.5 rounded-full bg-[#1a7f37]" /> 在线
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-[#f6f8fa] text-[#57606a]">
      <span className="w-1.5 h-1.5 rounded-full bg-[#8b949e]" /> 离线
    </span>
  )
);

const InstanceList = () => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery('instances', getInstances, { refetchInterval: 30000 });

  // accounts: 按 MT5 账户归组(同账户多图表合并); instances: 扁平图表级实例
  const accounts = data?.accounts || [];
  const summary = data?.summary || {};
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [keyword, setKeyword] = React.useState('');

  // 搜索: 账户 / 客户名称 / 创建者 任一命中
  const kw = keyword.trim().toLowerCase();
  const filteredAccounts = kw
    ? accounts.filter(a =>
        (a.accountLogin || '').toLowerCase().includes(kw) ||
        (a.accountName || '').toLowerCase().includes(kw) ||
        (a.customerName || '').toLowerCase().includes(kw) ||
        (a.creator || '').toLowerCase().includes(kw)
      )
    : accounts;

  const toggleExpand = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-40" />
        <div className="skeleton h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-md border border-[#d0d7de] p-8 text-center">
        <p className="text-[#cf222e]">获取实例列表失败</p>
        <button onClick={() => refetch()} className="mt-3 text-[#0969da] text-sm hover:underline">重试</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1f2328]">实例总览</h1>
          <p className="text-[#656d76] text-sm mt-1">按 MT5 账户归组展示（同一账户多图表合并，点击展开看图表实例）· 30 秒自动刷新</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e]" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索账户 / 客户名称 / 创建者"
              className="w-64 border border-[#d0d7de] rounded-md pl-9 pr-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            />
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1 text-sm text-[#0969da] border border-[#d0d7de] rounded-md px-3 py-1.5 bg-white hover:bg-[#f6f8fa]"
          >
            <FiRefreshCw /> 刷新
          </button>
        </div>
      </div>

      {/* 汇总卡片(按账户去重, 同账户多图表不重复计算) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
          <p className="text-[#656d76] text-xs mb-1">在线账户</p>
          <p className="text-xl font-semibold text-[#1f2328]">
            {summary.online ?? 0}<span className="text-sm font-normal text-[#656d76]"> / {summary.total ?? 0}</span>
          </p>
        </div>
        <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
          <p className="text-[#656d76] text-xs mb-1">总净值(在线)</p>
          <p className="text-xl font-semibold text-[#1f2328]">{fmtMoney(summary.totalEquity)}</p>
        </div>
        <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
          <p className="text-[#656d76] text-xs mb-1">当日盈亏(在线)</p>
          <p className={`text-xl font-semibold ${profitClass(summary.dayProfit)}`}>{fmtProfit(summary.dayProfit)}</p>
        </div>
        <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
          <p className="text-[#656d76] text-xs mb-1">图表实例</p>
          <p className="text-xl font-semibold text-[#1f2328]">
            {summary.instanceOnline ?? 0}<span className="text-sm font-normal text-[#656d76]"> / {summary.instanceTotal ?? 0}</span>
          </p>
        </div>
      </div>

      {/* 账户表格 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm overflow-hidden">
        {accounts.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <FiActivity className="mx-auto text-3xl text-[#8b949e] mb-3" />
            <p className="text-[#656d76] text-sm">暂无实例数据</p>
            <p className="text-[#8b949e] text-xs mt-1">EA 客户端运行并上报后, 实例会出现在这里</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <FiSearch className="mx-auto text-3xl text-[#8b949e] mb-3" />
            <p className="text-[#656d76] text-sm">没有匹配的账户</p>
            <p className="text-[#8b949e] text-xs mt-1">换个关键词试试（账户 / 客户名称 / 创建者）</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f6f8fa] text-[#656d76] text-xs">
                  <th className="text-left px-4 py-3 font-medium">账户</th>
                  <th className="text-left px-4 py-3 font-medium">创建者</th>
                  <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">客户名称</th>
                  <th className="text-right px-4 py-3 font-medium">净值</th>
                  <th className="text-right px-4 py-3 font-medium">当日盈亏</th>
                  <th className="text-right px-4 py-3 font-medium hidden md:table-cell">持仓</th>
                  <th className="text-left px-4 py-3 font-medium">状态</th>
                  <th className="text-left px-4 py-3 font-medium hidden md:table-cell">最近上报</th>
                  <th className="text-right px-4 py-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eaeef2]">
                {filteredAccounts.map((acct) => {
                  const isOpen = expanded.has(acct.accountKey);
                  return (
                    <React.Fragment key={acct.accountKey}>
                      <tr className="hover:bg-[#f6f8fa]">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {acct.instanceCount > 1 ? (
                              <button
                                onClick={() => toggleExpand(acct.accountKey)}
                                className="text-[#656d76] hover:text-[#1f2328] shrink-0"
                                title={isOpen ? '收起图表实例' : '展开图表实例'}
                              >
                                {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                              </button>
                            ) : (
                              <span className="w-4 shrink-0" />
                            )}
                            <div>
                              <div className="text-[#1f2328] font-medium">
                                {acct.accountLogin || acct.accountKey.replace('inst:', '')}
                                {acct.accountServer && <span className="text-[#656d76] font-normal"> · {acct.accountServer}</span>}
                              </div>
                              {acct.accountName && <div className="text-[#8b949e] text-xs">{acct.accountName}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[#1f2328]">{acct.creator || '-'}</span>
                          {acct.licenseStatus !== 'active' && (
                            <span className="ml-1 text-[10px] px-1 rounded bg-[#f6f8fa] text-[#57606a]">{acct.licenseStatus}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#57606a] hidden lg:table-cell">{acct.customerName || '-'}</td>
                        <td className="px-4 py-3 text-right text-[#1f2328]">{fmtMoney(acct.equity)}</td>
                        <td className={`px-4 py-3 text-right ${profitClass(acct.dayProfit)}`}>{fmtProfit(acct.dayProfit)}</td>
                        <td className="px-4 py-3 text-right text-[#57606a] hidden md:table-cell">{fmtNum(acct.positions, 0)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <OnlineBadge online={acct.isOnline} />
                            {acct.instanceCount > 1 && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#ddf4ff] text-[#0969da]" title={`${acct.instanceCount} 个图表实例, ${acct.instanceOnlineCount} 个在线`}>
                                <FiMonitor /> {acct.instanceCount} 图表
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#656d76] text-xs hidden md:table-cell">{fmtAgo(acct.lastReportAt)}</td>
                        <td className="px-4 py-3 text-right">
                          {acct.instances.length === 1 ? (
                            <Link
                              to={`/monitor/instances/${encodeURIComponent(acct.instances[0].instanceId)}`}
                              className="inline-flex items-center gap-1 text-[#0969da] text-xs hover:underline"
                            >
                              <FiEye /> 明细
                            </Link>
                          ) : (
                            <button
                              onClick={() => toggleExpand(acct.accountKey)}
                              className="inline-flex items-center gap-1 text-[#0969da] text-xs hover:underline"
                            >
                              <FiEye /> {isOpen ? '收起' : `${acct.instanceCount} 个实例`}
                            </button>
                          )}
                        </td>
                      </tr>
                      {/* 展开: 图表级实例子行 */}
                      {isOpen && acct.instances.map((inst) => (
                        <tr key={inst.instanceId} className="bg-[#f6f8fa]">
                          <td className="pl-12 pr-4 py-2" colSpan={1}>
                            <div className="text-[#57606a] text-xs font-mono">{inst.instanceId}</div>
                          </td>
                          <td className="px-4 py-2 text-[#8b949e] text-xs" colSpan={2}>图表实例</td>
                          <td className="px-4 py-2 text-right text-xs text-[#1f2328]">{fmtMoney(inst.equity)}</td>
                          <td className={`px-4 py-2 text-right text-xs ${profitClass(inst.dayProfit)}`}>{fmtProfit(inst.dayProfit)}</td>
                          <td className="px-4 py-2 text-right text-xs text-[#57606a] hidden md:table-cell">{fmtNum(inst.positions, 0)}</td>
                          <td className="px-4 py-2"><OnlineBadge online={inst.isOnline} /></td>
                          <td className="px-4 py-2 text-[#656d76] text-xs hidden md:table-cell">{fmtAgo(inst.lastReportAt)}</td>
                          <td className="px-4 py-2 text-right">
                            <Link
                              to={`/monitor/instances/${encodeURIComponent(inst.instanceId)}`}
                              className="inline-flex items-center gap-1 text-[#0969da] text-xs hover:underline"
                            >
                              <FiEye /> 明细
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstanceList;
